import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { callFn } from '../../lib/api';
import type { AppUser, Invitation, Role, UserHistory } from '../../lib/types';
import { useApp } from '../../app/AppContext';
import { DataTable, type Column } from '../../components/DataTable';

type Toast = (text: string, kind?: 'ok' | 'err' | 'info') => void;

// ─── Person history popup ────────────────────────────────────────────────────

const EVENT_ICON: Record<string, string> = {
  item_created: '📦', item_updated: '✏️', item_closed: '🔒', item_restored: '♻️', items_imported: '⬆️',
  reservation_created: '🛒', reservation_ready: '🎁', reservation_picked_up: '✅', reservation_cancelled: '✖️', reservation_released: '↩️', reservation_qty_changed: '🔢', reservation_reinstated: '♻️',
  role_changed: '🔑', status_changed: '🚦', invitation_sent: '✉️', invitation_cancelled: '🚫',
  media_added: '🖼️', media_removed: '🗑️', preferred_location_set: '📍',
};

function when(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Boil a user-agent down to "Browser · OS" for a compact device label.
function uaShort(ua: string | null): string {
  if (!ua) return 'Unknown device';
  let browser = 'Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  let os = '';
  if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Windows/.test(ua)) os = 'Windows';
  else if (/Linux/.test(ua)) os = 'Linux';
  return os ? `${browser} · ${os}` : browser;
}

type TimelineEntry = { ts: string; icon: string; title: string; meta?: string; active?: boolean };

function buildTimeline(h: UserHistory): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  for (const s of h.sessions) {
    const device = `${uaShort(s.userAgent)}${s.ip ? ' · ' + s.ip : ''}`;
    out.push({
      ts: s.signedInAt, icon: '🔓', title: 'Signed in', active: s.active,
      meta: s.active ? `${device} · active (last seen ${when(s.lastActiveAt)})` : device,
    });
    if (s.signedOutAt) out.push({ ts: s.signedOutAt, icon: '🔒', title: 'Signed out', meta: device });
  }
  for (const e of h.events) {
    const actor = e.by_self ? '' : (e.actor_name || e.actor_email || 'an admin');
    out.push({
      ts: e.created_at,
      icon: EVENT_ICON[e.event_type] || '•',
      title: e.summary,
      meta: actor ? `by ${actor}` : undefined,
    });
  }
  return out.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
}

function HistoryModal({ user, toast, onClose }: { user: AppUser; toast: Toast; onClose: () => void }) {
  const [hist, setHist] = useState<UserHistory | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const h = await callFn<UserHistory>('get_user_history', { id: user.id });
        if (alive) setHist(h);
      } catch (e) { if (alive) { toast(e instanceof Error ? e.message : 'Failed to load history', 'err'); onClose(); } }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const timeline = hist ? buildTimeline(hist) : [];

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ margin: 0 }}>{user.display_name || user.email}</h3>
            <div className="muted" style={{ fontSize: 13 }}>{user.email}</div>
            <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
              <span className="badge cat">{user.role.replace('_', '-')}</span>
              <span className={`badge ${user.status === 'active' ? 'new' : 'gray'}`}>{user.status}</span>
            </div>
          </div>
          <button className="btn ghost small" onClick={onClose}>Close</button>
        </div>

        {loading ? (
          <div className="loading" style={{ padding: 24 }}>Loading history…</div>
        ) : timeline.length === 0 ? (
          <div className="empty" style={{ padding: 24 }}>No recorded activity yet.</div>
        ) : (
          <ul className="timeline">
            {timeline.map((t, i) => (
              <li key={i} className="timeline-item">
                <span className="timeline-ico" aria-hidden="true">{t.icon}</span>
                <div className="timeline-body">
                  <div className="timeline-title">
                    {t.title}
                    {t.active && <span className="badge new" style={{ marginLeft: 8 }}>active</span>}
                  </div>
                  <div className="timeline-meta">{when(t.ts)}{t.meta ? ` · ${t.meta}` : ''}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Top-level so the "Invitation created" toast can't remount it and lose `link`.
function InviteModal({ isSuperAdmin, toast, onClose, onDone, onCreated }: { isSuperAdmin: boolean; toast: Toast; onClose: () => void; onDone: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('foster');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);

  async function send() {
    if (!email.includes('@')) { toast('Enter a valid email', 'err'); return; }
    setBusy(true);
    try {
      const res = await callFn<{ link: string }>('create_invitation', { email: email.trim(), role });
      setLink(res.link); toast('Invitation created', 'ok');
      onCreated(); // refresh the invitations list immediately (don't wait for Done)
    } catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); setBusy(false); }
  }

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Invite a volunteer</h3>
        {link ? (
          <div className="stack">
            <div className="notice ok">Invitation emailed. You can also share this link:</div>
            <input readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
            <button className="btn" onClick={onDone}>Done</button>
          </div>
        ) : (
          <div className="stack">
            <label className="field">Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
            <label className="field">Role
              <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                <option value="foster">foster</option>
                {isSuperAdmin && <option value="admin">admin</option>}
                {isSuperAdmin && <option value="super_admin">super_admin</option>}
              </select>
            </label>
            <div className="row">
              <button className="btn secondary" onClick={onClose}>Cancel</button>
              <button className="btn" onClick={send} disabled={busy}>{busy ? 'Sending…' : 'Send invite'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function People() {
  const { isSuperAdmin, me, toast } = useApp();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [invites, setInvites] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [historyUser, setHistoryUser] = useState<AppUser | null>(null);

  async function load() {
    setLoading(true);
    try {
      setUsers(await callFn<AppUser[]>('list_users'));
      setInvites(await callFn<Invitation[]>('list_invitations'));
    } catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, []);

  async function changeRole(id: string, role: Role) {
    try { await callFn('set_user_role', { id, role }); toast('Role updated', 'ok'); void load(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }
  async function toggleStatus(u: AppUser) {
    const status = u.status === 'active' ? 'disabled' : 'active';
    try { await callFn('set_user_status', { id: u.id, status }); toast('Updated', 'ok'); void load(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }
  async function cancelInvite(iv: Invitation) {
    if (!confirm(`Cancel the invitation for ${iv.email}? The invite link will stop working.`)) return;
    try { await callFn('cancel_invitation', { id: iv.id }); toast('Invitation cancelled', 'ok'); void load(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }
  function goAllocation(u: AppUser, status: 'reserved' | 'picked_up') {
    navigate(`/admin/allocation?user=${encodeURIComponent(u.id)}&name=${encodeURIComponent(u.display_name || u.email)}&status=${status}`);
  }

  if (loading) return <div className="loading">Loading…</div>;

  const userCols: Column<AppUser>[] = [
    { key: 'name', label: 'Name', get: (u) => u.display_name || '—', onClick: (u) => setHistoryUser(u) },
    { key: 'email', label: 'Email', get: (u) => u.email },
    {
      key: 'role', label: 'Role', get: (u) => u.role,
      render: (u) => (isSuperAdmin && u.id !== me?.id
        ? <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value as Role)}>
            <option value="foster">foster</option><option value="admin">admin</option><option value="super_admin">super_admin</option>
          </select>
        : <span className="badge cat">{u.role.replace('_', '-')}</span>),
    },
    { key: 'reserved', label: 'Reserved (mo.)', get: (u) => u.monthly_reserved ?? 0, numeric: true, align: 'right', onClick: (u) => goAllocation(u, 'reserved') },
    { key: 'picked_up', label: 'Picked up (mo.)', get: (u) => u.monthly_picked_up ?? 0, numeric: true, align: 'right', onClick: (u) => goAllocation(u, 'picked_up') },
    { key: 'status', label: 'Status', get: (u) => u.status, render: (u) => <span className={`badge ${u.status === 'active' ? 'new' : 'gray'}`}>{u.status}</span> },
  ];

  const inviteCols: Column<Invitation>[] = [
    { key: 'email', label: 'Email', get: (i) => i.email },
    { key: 'role', label: 'Role', get: (i) => i.role },
    { key: 'status', label: 'Status', get: (i) => (i.accepted_at ? 'accepted' : 'pending'), render: (i) => <span className={`badge ${i.accepted_at ? 'new' : 'reserved'}`}>{i.accepted_at ? 'accepted' : 'pending'}</span> },
    { key: 'expires', label: 'Expires', get: (i) => i.expires_at, render: (i) => new Date(i.expires_at).toLocaleDateString() },
  ];

  return (
    <>
      <h1 className="page-title">People</h1>
      <p className="page-sub">Volunteers and admins. Click a name to see their history, or a monthly count to see those reservations on the Allocation board.</p>
      <div className="toolbar"><button className="btn" onClick={() => setInviteOpen(true)}>+ Invite volunteer</button></div>

      <DataTable
        columns={userCols}
        rows={users}
        rowKey={(u) => u.id}
        exportName="people"
        actions={(u) => u.id !== me?.id ? <button className="btn ghost small" onClick={() => toggleStatus(u)}>{u.status === 'active' ? 'Disable' : 'Enable'}</button> : null}
      />

      <h3 style={{ color: 'var(--navy)', marginTop: 26 }}>Invitations</h3>
      <DataTable
        columns={inviteCols}
        rows={invites}
        rowKey={(i) => i.id}
        exportName="invitations"
        empty="No invitations yet."
        actions={(iv) => !iv.accepted_at ? <button className="btn ghost small" onClick={() => cancelInvite(iv)}>Cancel</button> : null}
      />

      {inviteOpen && <InviteModal isSuperAdmin={isSuperAdmin} toast={toast} onCreated={() => void load()} onClose={() => { setInviteOpen(false); void load(); }} onDone={() => { setInviteOpen(false); void load(); }} />}
      {historyUser && <HistoryModal user={historyUser} toast={toast} onClose={() => setHistoryUser(null)} />}
    </>
  );
}
